import { Input, Select } from "antd";
import { debounce, uniq } from "lodash";
import { useEffect, useRef, useState } from "react";
import { IoClose, IoSearchOutline } from "react-icons/io5";
import apiFactory from "../../../../api";
import searchImg from "../../../../assets/search.png";
import {
  CHATTING,
  MESSAGE_STATUS,
  PANEL_STATUS,
} from "../../../../config/Constant";
import { useChatContext } from "../../../../context/ChatContext";
import { useMenuContext } from "../../../../context/MenuContext";
import { useInfoUser } from "../../../../store/UserStore";
import { openNotificationError, processFileUrl } from "../../../../utils/Utils";
import { SearchMessageList } from "./SearchMessageList";

const SearchPanel = ({ jumpToMessage, openModal }) => {
  const debouncedSearchValue = useRef(null);

  const { user, languageMap } = useInfoUser();
  const { setPanelStatus, setSelectedMessageId, setSelectedMailId } =
    useMenuContext();
  const { selectedConversation } = useChatContext();
  const abortControllerRef = useRef(new AbortController());

  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState(null);
  const [searchValue, setSearchValue] = useState(null);

  const [messageList, setMessageList] = useState([]);
  const [isMoreMessage, setIsMoreMessage] = useState(false);
  const [userList, setUserList] = useState([]);

  const getListUserFromMessage = async (messageList) => {
    //get all senderIds and quotation senderIds
    const userIds = [];
    for (const message of messageList) {
      userIds?.push(message?.senderId);
      if (message?.quotation) {
        userIds?.push(message?.quotation?.senderId);
      }
    }

    if (userIds?.length === 0) return;

    const userResult = await apiFactory.conversationApi.getMemberList({
      conversationId: selectedConversation?.conversationId,
      memberIds: uniq(userIds),
    });

    if (userResult?.status !== 200) {
      return openNotificationError(userResult?.message);
    }

    return userResult?.data;
  };

  const initMessageInChat = async (value) => {
    if (!selectedConversation?.conversationId) return;

    if (!value || value === "") {
      setMessageList([]);
      setUserList([]);
      return;
    }

    setIsLoading(true);
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      const result = await apiFactory.messageApi.getList(
        {
          limit: CHATTING.MESSAGE_LIMIT,
          maxTs: messageList?.[messageList?.length - 1]?.createdAt,
          search: {
            conversationId: selectedConversation?.conversationId,
            content: value,
            contentTypes: [
              MESSAGE_STATUS.TEXT,
              // MESSAGE_STATUS.FILE,
              // MESSAGE_STATUS.AUDIO,
              MESSAGE_STATUS.IMAGE,
            ],
          },
        },
        { signal: abortControllerRef.current.signal },
      );

      if (result?.status !== 200) {
        openNotificationError(result?.message);
        return;
      }

      if (result?.data?.length === 0) {
        setUserList([]);
      } else {
        const userList = await getListUserFromMessage(result?.data);
        setUserList([...(userList || [])]);
      }

      let messages = result?.data
        ?.map((item) => ({
          ...item,
          isSelf: item?.senderId === user?.userId,
          username: userList?.filter((u) => u?.userId === item?.senderId)?.[0]
            ?.name,
          conversationId: selectedConversation?.conversationId,
          photos: processFileUrl(
            item?.photos,
            "/conversation/",
            selectedConversation?.conversationId,
          ),
          progress: 1,
        }))
        .reverse();

      const sortedMessages = [...(messages || [])].sort(
        (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt),
      );

      setMessageList([...sortedMessages]);
      setIsLoading(false);
      setIsMoreMessage(messages?.length === CHATTING.MESSAGE_LIMIT);
    } catch (error) {
      if (error.name !== "AbortError") {
        return new Error();
      }
    }
  };

  const fetchMoreMessageInChat = async () => {
    if (!selectedConversation?.conversationId) return;

    if (!searchValue || searchValue === "") {
      setMessageList([]);
      setUserList([]);
      return;
    }

    setIsLoading(true);
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      const result = await apiFactory.messageApi.getList(
        {
          limit: CHATTING.MESSAGE_LIMIT,
          maxTs: messageList?.[messageList?.length - 1]?.createdAt,
          search: {
            conversationId: selectedConversation?.conversationId,
            content: searchValue,
            contentTypes: [
              MESSAGE_STATUS.TEXT,
              // MESSAGE_STATUS.FILE,
              // MESSAGE_STATUS.AUDIO,
              MESSAGE_STATUS.IMAGE,
            ],
          },
        },
        { signal: abortControllerRef.current.signal },
      );

      if (result?.status !== 200) {
        openNotificationError(result?.message);
        return;
      }

      const userList = await getListUserFromMessage(result?.data);
      setUserList((prev) => [...(prev || []), ...(userList || [])]);

      let messages = result?.data
        ?.map((item) => ({
          ...item,
          isSelf: item?.senderId === user?.userId,
          username: userList?.filter((u) => u?.userId === item?.senderId)?.[0]
            ?.name,
          conversationId: selectedConversation?.conversationId,
          photos: processFileUrl(
            item?.photos,
            "/conversation/",
            selectedConversation?.conversationId,
          ),
          progress: 1,
        }))
        .reverse();

      const sortedMessages = [...(messages || [])].sort(
        (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt),
      );

      setMessageList((prev) => [...(prev || []), ...sortedMessages]);
      setIsLoading(false);
      setIsMoreMessage(messages?.length === CHATTING.MESSAGE_LIMIT);
    } catch (error) {
      if (error.name !== "AbortError") {
        return new Error();
      }
    }
  };

  const onChangeSearch = (e) => {
    setSearchText(e?.target?.value);
    debouncedSearchValue?.current?.(e?.target?.value);
  };

  useEffect(() => {
    /** clear old ref or state */
    setSelectedMessageId(null);
    setSelectedMailId(null);
    setMessageList([]);
    setUserList([]);
    setSearchText(null);
    setSearchValue(null);

    debouncedSearchValue.current = debounce(async (value) => {
      setSearchValue(value);
      await initMessageInChat(value);
    }, 500);

    /** cleanup when unmount */
    return () => {
      debouncedSearchValue.current?.cancel?.();
      abortControllerRef.current?.abort?.();
    };
  }, [selectedConversation?.conversationId]);

  return (
    <div className="chat-right-panel h-screen bg-white border-l flex flex-col">
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-center h-[67px]">
        <span className="font-semibold text-[18px]">
          {languageMap?.["menu.conversation.searchInChat"] ?? "Search in chat"}
        </span>
        <button
          className="absolute right-[16px]"
          onClick={() => {
            setPanelStatus((prev) => {
              if (prev === PANEL_STATUS.SEARCH_CHAT) return null;
              return PANEL_STATUS.GROUP_INFO;
            });
          }}
        >
          <IoClose size={25} />
        </button>
      </div>
      {/* Search */}
      <div className="flex flex-col gap-[10px] w-[350px] py-[16px] px-[16px] h-[106px]">
        <Input
          value={searchText}
          placeholder={
            languageMap?.["menu.searchConversation.placeholder"] || "Search..."
          }
          onChange={onChangeSearch}
          className="w-full"
          prefix={<IoSearchOutline size={15} />}
          allowClear
        />
        <div className="flex gap-[10px] items-center">
          <div>Filter:</div>
          <Select
            disabled
            placeholder={languageMap?.["menu.sender.default"] ?? "Sender name"}
            allowClear
            className="w-full"
          />
        </div>
      </div>
      {/* Message search list */}
      <div className="">
        {!searchValue || searchValue === "" ? (
          <div className="flex flex-col items-center">
            <img src={searchImg} alt="Cover" className="w-[40%]" />
            <div className="px-[44px] text-center text-[#5a6981]">
              Enter keywords to start searching for messages and files in the
              conversation
            </div>
          </div>
        ) : (
          <SearchMessageList
            jumpToMessage={jumpToMessage}
            messageList={messageList}
            userList={userList}
            fetchMoreMessageInChat={fetchMoreMessageInChat}
            setIsLoading={setIsLoading}
            isMoreMessage={isMoreMessage}
            searchValue={searchValue}
          />
        )}
      </div>
    </div>
  );
};

export { SearchPanel };
